import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import './Onboarding.css';

const COUNTRY_CODES = [
  { flag: '🇩🇪', code: '+49' }, { flag: '🇺🇸', code: '+1'  }, { flag: '🇬🇧', code: '+44' },
  { flag: '🇫🇷', code: '+33' }, { flag: '🇮🇹', code: '+39' }, { flag: '🇪🇸', code: '+34' },
  { flag: '🇦🇹', code: '+43' }, { flag: '🇨🇭', code: '+41' }, { flag: '🇵🇱', code: '+48' },
  { flag: '🇳🇱', code: '+31' }, { flag: '🇸🇪', code: '+46' }, { flag: '🇳🇴', code: '+47' },
  { flag: '🇩🇰', code: '+45' }, { flag: '🇫🇮', code: '+358'},{ flag: '🇧🇪', code: '+32' },
  { flag: '🇵🇹', code: '+351'},{ flag: '🇬🇷', code: '+30' }, { flag: '🇯🇵', code: '+81' },
  { flag: '🇰🇷', code: '+82' }, { flag: '🇨🇳', code: '+86' }, { flag: '🇮🇳', code: '+91' },
  { flag: '🇧🇷', code: '+55' }, { flag: '🇦🇺', code: '+61' }, { flag: '🇷🇺', code: '+7'  },
  { flag: '🇺🇦', code: '+380'},{ flag: '🇹🇷', code: '+90' }, { flag: '🇮🇱', code: '+972'},
  { flag: '🇿🇦', code: '+27' }, { flag: '🇳🇬', code: '+234'},{ flag: '🇲🇽', code: '+52' },
  { flag: '🇦🇷', code: '+54' },
];

const BTN_LABELS = ['Send code', 'Verify'];
const TOTAL_STEPS = 2;

export default function Onboarding() {
  const history = useHistory();

  const [step, setStep]       = useState(0);
  const [countryCode, setCC]  = useState('+49');
  const [phone, setPhone]     = useState('');
  const [digits, setDigits]   = useState(['','','','','','']);
  const [resendSec, setResendSec] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const digitRefs = useRef([]);

  // ── Resend countdown ──
  useEffect(() => {
    if (resendSec <= 0) return;
    const id = setTimeout(() => setResendSec(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendSec]);

  const isNextEnabled = (() => {
    if (step === 0) return phone.trim().length > 0;
    return digits.every(d => d !== '');
  })();

  const goTo = (idx) => {
    setAnimKey(k => k + 1);
    setStep(idx);
  };

  const handleNext = () => {
    if (step === 0) { goTo(1); setResendSec(30); }
    else history.push('/profile');
  };

  const handleBack = () => goTo(step - 1);

  // ── OTP digit input ──
  const onDigitInput = (i, val) => {
    const cleaned = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = cleaned;
    setDigits(next);
    if (cleaned && i < 5) digitRefs.current[i + 1]?.focus();
  };

  const onDigitKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      const next = [...digits];
      next[i - 1] = '';
      setDigits(next);
      digitRefs.current[i - 1]?.focus();
    }
  };

  const onDigitPaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
    const next = ['','','','','',''];
    for (let i = 0; i < 6; i++) next[i] = paste[i] || '';
    setDigits(next);
    const lastIdx = Math.min(paste.length - 1, 5);
    digitRefs.current[lastIdx]?.focus();
  };

  const maskedPhone = `${countryCode} *** **${phone.slice(-2)}`;

  return (
    <div className="ob-root">

      {/* ── Progress bar (sits under global navbar) ── */}
      <div className="ob-progress">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            className={`ob-pb${i < step ? ' done' : i === step ? ' active' : ''}`}
          />
        ))}
        <span className="ob-pb-label">{step + 1} / {TOTAL_STEPS}</span>
      </div>

      {/* ── Step content ── */}
      <div className="ob-body" key={animKey}>

        {/* Step 0: Phone */}
        {step === 0 && (
          <div className="ob-step">
            <div className="ob-icon">
              <svg viewBox="0 0 24 24"><path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1.003 1.003 0 011.01-.24c1.12.37 2.33.57 3.58.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.46.57 3.58.11.33.03.7-.25 1.02l-2.2 2.19z"/></svg>
            </div>
            <h1 className="ob-title">Verify your phone number</h1>
            <p className="ob-desc">To ensure fair results, each participant can only vote once. We use your phone number to verify your identity.</p>

            <div className="ob-phone-group">
              <select className="ob-country" value={countryCode} onChange={e => setCC(e.target.value)}>
                {COUNTRY_CODES.map(({ flag, code }) => (
                  <option key={code} value={code}>{flag} {code}</option>
                ))}
              </select>
              <input
                className="ob-phone-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Phone number"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>

            <div className="ob-privacy">
              <svg viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 6h2v2h-2V7zm0 4h2v6h-2v-6z"/></svg>
              <p>Your phone number is only used for verification and will not be shared with third parties. We never store your number after verification is complete.</p>
            </div>

            <div className="ob-trust">
              <div className="ob-trust-item">
                <svg viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/></svg>
                End-to-end encrypted
              </div>
              <div className="ob-trust-item">
                <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                GDPR compliant
              </div>
            </div>
          </div>
        )}

        {/* Step 1: OTP */}
        {step === 1 && (
          <div className="ob-step">
            <div className="ob-icon">
              <svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/></svg>
            </div>
            <h1 className="ob-title">Enter verification code</h1>
            <p className="ob-desc">We sent a 6-digit code to <strong className="ob-strong">{maskedPhone}</strong>. Enter it below to continue.</p>

            <div className="ob-digits">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => digitRefs.current[i] = el}
                  className={`ob-digit${d ? ' filled' : ''}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={e => onDigitInput(i, e.target.value)}
                  onKeyDown={e => onDigitKeyDown(i, e)}
                  onPaste={onDigitPaste}
                />
              ))}
            </div>

            <div className="ob-resend">
              <span className="ob-resend-text">Didn't receive a code? </span>
              {resendSec > 0 ? (
                <span className="ob-resend-btn disabled">Resend in 0:{String(resendSec).padStart(2, '0')}</span>
              ) : (
                <button className="ob-resend-btn" onClick={() => setResendSec(30)}>Resend code</button>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Fixed bottom buttons ── */}
      <div className="ob-btns">
        {step > 0 && (
          <button className="ob-btn-back" onClick={handleBack}>Back</button>
        )}
        <button
          className="ob-btn-next"
          onClick={handleNext}
          disabled={!isNextEnabled}
        >
          {BTN_LABELS[step]}
        </button>
      </div>

    </div>
  );
}
